import * as React from "react";
import { useCallback, useEffect, useMemo } from "react";
import { Slide, styled } from "@mui/material";
import { RosebudColorMap } from "../rosebud/rosebudColorMap";
import { DiseaseCategoryType } from "fe-common/graphql";
import { ClusteredSuggestions, Suggestion } from "../hooks/useSuggestions";
import { MobileView } from "../../../util/aKaunGeneralUtil";
import { RoundedActionButton } from "../common/roundedActionButton";
import { ACTION, CATEGORY, evt } from "fe-common/analytics/analytics";
import { usePulse } from "../../../util/usePule";
import { KahunColorMap } from "fe-common/ui/assets/colorMap";
import { PatientCaseChange, usePatientCase } from "../usePatientCase";
import { useDiagnosticNavigation } from "../useDiagnosticNavigation";
import { useScreenSize } from "../util/useScreenSize";
import { RosebudMobileBottomNavigationHeight } from "../common/rosebudButtomNavigationButton";
import { useBuildPlanPanels } from "../useBuildPlanPanels";
import { PlanTabs } from "./planTabs";
import { PlanClusters } from "./planClusters";
import { AgentRoute } from "../util/agentRoutes";
import { useRouteHelper } from "../useRouteHelper";
import { PlanTabsEnum } from "../editPanel/editPanelPage";
import { HowItWorksTopLink } from "../santana/howItWorksTopLink";
import { useGetDiseasesInPlan } from "../hooks/useGetDiseasesInPlan";

//region [[ Styles ]]

const PlanCategoryView = styled("div")(
  ({ theme, isEmbedded }: { theme?: any; isEmbedded: string }) => ({
    background: RosebudColorMap.White,
    margin: 0,
    padding: 0,
    display: "flex",
    flexDirection: "column",
    minHeight: "230px",
    minWidth: "200px",
  }),
);

const PlanTitleArea = styled("div")(
  ({ theme, isEmbedded }: { theme?: any; isEmbedded: string }) => ({
    fontWeight: 500,
    fontSize: "16px",
    margin: "10px",
    marginBottom: "20px",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end", // Make the show more button align right
    flexWrap: "wrap-reverse",
    [theme.breakpoints.down(MobileView)]: {
      fontSize: "1.2em",
      marginTop: isEmbedded === "true" ? "0px" : "11px",
      paddingRight: isEmbedded === "true" ? "0px" : "11px",
      paddingLeft: isEmbedded === "true" ? "0px" : "11px",
    },
  }),
);

const ShowMore = styled(RoundedActionButton)({
  padding: "4px 7px",
  margin: "0 2px",
  textTransform: "none",
});

// Add a container for the title to make it wrap if it doest feet
const SectionTitleContainer = styled("div")({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  flexGrow: 1, // Wrap an entire row
});

const SectionTitle = styled("div")(({ theme }) => ({
  width: "100%",
  minHeight: "20px",
  [theme.breakpoints.down(MobileView)]: {
    fontWeight: 700,
    fontSize: "16px",
  },
}));

const PlanItems = styled("div")(
  ({ isembedded }: { isembedded?: "true" | "false" }) => ({
    backgroundColor:
      isembedded === "true" ? "transparent" : KahunColorMap.AccentLight,
    padding: isembedded === "true" ? "0px" : "24px",
    paddingBottom:
      isembedded === "true" ? 0 : `${RosebudMobileBottomNavigationHeight}px`,
    paddingTop: isembedded === "true" ? "0px" : "30px",
    marginLeft: isembedded === "true" ? "-10px" : "0px",
    minHeight: "300px",
    height: "100%",
  }),
);

const HowItWorks = styled(HowItWorksTopLink)({
  "& .how-it-works-link": {
    color: KahunColorMap.FrequencyLabel,
  },
});

const PlanArea = styled("div")(({ theme }) => ({
  display: "flex",
  flexFlow: "column",
  height: "100%",
}));

const NoSuggestionsText = styled("div")({
  display: "flex",
  width: "100%",
  height: "100%",
  paddingLeft: "20px",
  paddingRight: "20px",
  flexFlow: "column",
  justifyContent: "center",
  textAlign: "center",
  color: RosebudColorMap.gray2,
  minHeight: "100px",
});

//endregion [[ Styles ]]

//region [[ Props ]]
interface PlanCategoryProps {
  className?: string;
  category: DiseaseCategoryType;
  suggestions: Suggestion[];
  clusteredSuggestions: ClusteredSuggestions[];
  title?: string;
  maxItems?: number;
  showMore?: boolean;
  exportData?: boolean;
  onShowMore?: (cat: DiseaseCategoryType) => void;
  onShowLess?: (cat: DiseaseCategoryType) => void;
  onChange?: () => void;
  isLoading: boolean;
  eventCategory: CATEGORY;
  isEmbedded?: boolean;
  onChangeCategory?: (DiseaseCategoryType) => void;
}
//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const PlanSection = ({
  suggestions,
  category,
  maxItems,
  showMore,
  exportData = true,
  onShowMore,
  onShowLess,
  onChange,
  isLoading,
  isEmbedded,
  onChangeCategory,
  clusteredSuggestions,
  ...props
}: PlanCategoryProps) => {
  const totalSuggestionsCount = React.useMemo(
    () => suggestions.filter((sg) => sg.category === category).length,
    [suggestions, category],
  );
  const { isMobile } = useScreenSize();
  const { navigateTo } = useRouteHelper();
  const { setNextRouteNavigationDisabled } = useDiagnosticNavigation();

  // Keep the diagnose button disabeld
  useEffect(() => {
    setNextRouteNavigationDisabled(true);
    return () => setNextRouteNavigationDisabled(false);
  }, [setNextRouteNavigationDisabled]);

  const { doChangesToPatientCase, doChangesToPending } = usePatientCase();

  const { pulse, withAnimation } = usePulse({
    disabled: !isLoading,
  });

  const filteredSuggestionsByCategory = React.useMemo(
    () =>
      suggestions
        .filter((sg) => sg.category === category)
        .slice(0, maxItems || 10),
    [suggestions, category, maxItems],
  );

  const setLabs = () => {
    if (isMobile && !isEmbedded) {
      navigateTo({
        route: AgentRoute.RecommendedPlan + "/" + PlanTabsEnum.LABS,
      });
    } else {
      onChangeCategory(DiseaseCategoryType.OutcomeTest);
    }
  };

  const setImaging = () => {
    if (isMobile && !isEmbedded) {
      navigateTo({
        route: AgentRoute.RecommendedPlan + "/" + PlanTabsEnum.IMAGING,
      });
    } else {
      onChangeCategory(DiseaseCategoryType.Imaging);
    }
  };

  const { labSuggestionsPanels: labsPanels, loading } = useBuildPlanPanels(
    suggestions,
    clusteredSuggestions,
    category,
  );

  const handleShowMoreLessClick = useCallback(() => {
    evt({
      category: props.eventCategory,
      action: showMore ? ACTION.PLAN_SHOW_LESS : ACTION.PLAN_SHOW_MORE,
      label: props.title,
    });

    if (showMore) {
      return onShowLess(category);
    } else {
      return onShowMore(category);
    }
  }, [
    category,
    onShowMore,
    onShowLess,
    showMore,
    props.title,
    props.eventCategory,
  ]);

  const onPlanItemChange = (change: PatientCaseChange) => {
    if (!isMobile) {
      doChangesToPending([change]);
    } else {
      doChangesToPatientCase([change]);
    }
    setNextRouteNavigationDisabled(false);
    onChange && onChange();
    console.log(loading);
  };

  const showMoreButton = useMemo(
    () =>
      onShowMore &&
      !isLoading &&
      (showMore ||
        totalSuggestionsCount > filteredSuggestionsByCategory.length),
    [
      onShowMore,
      isLoading,
      showMore,
      totalSuggestionsCount,
      filteredSuggestionsByCategory,
    ],
  );

  const embedded = isEmbedded ? "true" : "false";

  const noResults = filteredSuggestionsByCategory.length === 0;

  const diseasesInPlan = useGetDiseasesInPlan();

  const noItemsText = diseasesInPlan?.length ? (
    <NoSuggestionsText>
      Oops... something went wrong. Please try reloading the page. If the
      problem persist, please contact
      <a href="mailto://support@kahun.com">support@kahun.com</a>
    </NoSuggestionsText>
  ) : (
    <NoSuggestionsText>
      No diseases in list, please add a diseases to include in plan
    </NoSuggestionsText>
  );
  return (
    <PlanCategoryView className={props.className} isEmbedded={embedded}>
      {!isEmbedded && (
        <PlanTitleArea isEmbedded={embedded}>
          <SectionTitleContainer>
            <HowItWorks />
            <SectionTitle>{props.title || category}</SectionTitle>
          </SectionTitleContainer>
          {showMoreButton && (
            <ShowMore
              className={`${isLoading ? pulse : ""} ${withAnimation}`}
              onClick={handleShowMoreLessClick}
              displayText={showMore ? "Show Less" : "Show More"}
            />
          )}
        </PlanTitleArea>
      )}
      <PlanArea>
        <PlanTabs
          labsClicked={setLabs}
          imagingClicked={setImaging}
          selected={category}
          isEmbedded={isEmbedded}
        />
        <PlanItems isembedded={embedded}>
          {!isLoading && noResults ? (
            <Slide direction="up" in mountOnEnter unmountOnExit>
              {noItemsText}
            </Slide>
          ) : (
            <PlanClusters
              isEmbedded={isEmbedded}
              loading={loading}
              onPlanItemChange={onPlanItemChange}
              clusters={labsPanels}
              maxItems={maxItems}
            />
          )}
        </PlanItems>
      </PlanArea>
    </PlanCategoryView>
  );
};
